import { routeViewUsesActualCompletedRoute } from '../constants/routeViewMap';
import { fetchMapboxDrivingRoute } from './mapboxDirections';

const moment = require('moment-timezone');

async function getSimpleJobDirection(jobDetail) {
  const startLon = jobDetail.start_lon / 1000000.0;
  const startLat = jobDetail.start_lat / 1000000.0;
  const endLon = jobDetail.end_lon / 1000000.0;
  const endLat = jobDetail.end_lat / 1000000.0;
  return fetchMapboxDrivingRoute(startLon, startLat, endLon, endLat);
}

/** Mapbox trip lines + connectors for Route View (shared by table load and map). */
export const computeRouteViewMapLayers = async (jobs, timezone) => {
  if (!jobs?.length) {
    return { directions: [], connectors: [], fitBounds: null };
  }

  const data = [];
  const connectors = [];
  const useActualCompletedRoute = routeViewUsesActualCompletedRoute();

  for (let i = 0; i < jobs.length; i++) {
    const jobDetail = jobs[i];
    const directionData = await getSimpleJobDirection(jobDetail);
    let direction = directionData?.direction;
    if (useActualCompletedRoute && jobDetail.status == 8 && jobDetail.route_locations?.layers?.length > 0) {
      const activeLayer = jobDetail.route_locations?.layers?.filter(layer => layer.id == 'activeJob');
      if (activeLayer.length > 0) {
        direction = activeLayer[0].source.data.geometry.coordinates;
      }
    }
    const useCompletedGpsPoints =
      useActualCompletedRoute && jobDetail.status == 8 && jobDetail.route_locations?.points?.pickup;
    const start_lat = useCompletedGpsPoints
      ? jobDetail.route_locations.points.pickup.latitude
      : jobDetail.start_lat / 1000000.0;
    const start_lon = useCompletedGpsPoints
      ? jobDetail.route_locations.points.pickup.longitude
      : jobDetail.start_lon / 1000000.0;
    const end_lat =
      useCompletedGpsPoints && jobDetail.route_locations?.points?.dropoff
        ? jobDetail.route_locations.points.dropoff.latitude
        : jobDetail.end_lat / 1000000.0;
    const end_lon =
      useCompletedGpsPoints && jobDetail.route_locations?.points?.dropoff
        ? jobDetail.route_locations.points.dropoff.longitude
        : jobDetail.end_lon / 1000000.0;

    data.push({
      id: jobDetail.id,
      start_lat,
      start_lon,
      end_lat,
      end_lon,
      start_time: moment
        .utc(
          jobDetail.status == 8 && moment(jobDetail.pickup_time).isValid()
            ? jobDetail.pickup_time
            : jobDetail.reservation_time
        )
        .tz(timezone)
        .format('hh:mm A'),
      end_time: moment
        .utc(
          jobDetail.status == 8 && moment(jobDetail.pickup_time).isValid() && moment(jobDetail.dropoff_time).isValid()
            ? jobDetail.dropoff_time
            : jobDetail.reservation_time
        )
        .add(
          jobDetail.status == 8 && moment(jobDetail.pickup_time).isValid() && moment(jobDetail.dropoff_time).isValid()
            ? 0
            : directionData?.duration || 0,
          'minutes'
        )
        .tz(timezone)
        .format('hh:mm A'),
      status: jobDetail.status,
      direction,
    });

    const nextJob = jobs[i + 1];
    if (nextJob) {
      const nextStartLon = nextJob.start_lon / 1000000.0;
      const nextStartLat = nextJob.start_lat / 1000000.0;
      const connectorData = await fetchMapboxDrivingRoute(end_lon, end_lat, nextStartLon, nextStartLat);
      if (connectorData?.direction) {
        connectors.push({
          id: `connector-${jobDetail.id}-${nextJob.id}`,
          fromJobId: jobDetail.id,
          toJobId: nextJob.id,
          direction: connectorData.direction,
          miles: connectorData.distance ?? 0,
          minutes: connectorData.duration ?? 0,
        });
      }
    }
  }

  const lngArray = [
    ...data.map(item => [item.start_lon, item.end_lon]).flat(),
    ...connectors.map(item => item.direction.map(coord => coord[0])).flat(),
  ];
  const latArray = [
    ...data.map(item => [item.start_lat, item.end_lat]).flat(),
    ...connectors.map(item => item.direction.map(coord => coord[1])).flat(),
  ];

  let fitBounds = null;
  if (lngArray.length > 0 && latArray.length > 0) {
    fitBounds = [
      [Math.min(...lngArray), Math.min(...latArray)],
      [Math.max(...lngArray), Math.max(...latArray)],
    ];
  }

  return { directions: data, connectors, fitBounds };
};
